/**
 * Shared constants used by the web frontend, React Native mobile app, and Node.js backend.
 * Platform-specific values (e.g. API_BASE_URL) are intentionally omitted here and
 * live in each platform's own constants file.
 *
 * Uses CommonJS exports so this file can be consumed by both Node.js (require) and
 * bundled apps (webpack / Metro — both handle CJS→ESM interop transparently).
 */

// App Info
const APP_NAME = 'Car Alert';
const APP_DESCRIPTION = 'Sistema de Rastreamento e Alerta de Veículos Roubados';
const BRAND_TAGLINE = 'Vigia Auto';

// App Colors — kept in sync with frontend/src/utils/constants.js
const COLORS = {
  primary: '#4f8ef7',
  secondary: '#7c3aed',
  danger: '#ef4444',
  success: '#10b981',
  warning: '#f59e0b',
  white: '#ffffff',
  black: '#000000',
  gray: '#94a3b8',
  lightGray: '#1e293b',
  background: '#0a0e1a',
  backgroundCard: '#111827',
  backgroundSecondary: '#1a2035',
  text: '#e2e8f0',
  textSecondary: '#b0bec5',
  border: '#1e293b',
  cyan: '#00d9ff',
  cyanGlow: '#00d9ff',
  orange: '#ff6b35',
  mint: '#20c997',
  purple: '#7c3aed',
};

// Security Colors
const SECURITY_COLORS = {
  verified: '#10b981',
  protected: '#4f8ef7',
  alert: '#ef4444',
  warning: '#f59e0b',
  locked: '#6b7280',
  success: '#10b981',
};

// Alert Radius Configuration
const NOTIFICATION_RADIUS_KM = 15;
const DEFAULT_ALERT_RADIUS_KM = NOTIFICATION_RADIUS_KM;
const MIN_ALERT_RADIUS_KM = 5;
const MAX_ALERT_RADIUS_KM = 100;

// Storage Keys — used by AsyncStorage (mobile) and localStorage (web)
const STORAGE_KEYS = {
  AUTH_TOKEN: '@car_alert:token',
  USER_DATA: '@car_alert:user',
};

// Security Storage Keys
const SECURITY_STORAGE_KEYS = {
  PIN_HASH: '@car_alert:pin_hash',
  FACE_DATA: '@car_alert:face_data',
  INACTIVITY_TIMEOUT: '@car_alert:inactivity_timeout',
};

// Inactivity timeout options (milliseconds; 0 = never lock)
const INACTIVITY_OPTIONS = [
  { label: '1 minuto', value: 60000 },
  { label: '5 minutos', value: 300000 },
  { label: '10 minutos', value: 600000 },
  { label: '30 minutos', value: 1800000 },
  { label: 'Nunca', value: 0 },
];

// ── Vehicle catalogue ─────────────────────────────────────────────────────────

const BRANDS = [
  'Audi', 'BMW', 'BYD', 'Caoa Chery', 'Chery', 'Chevrolet', 'Citroën', 'Dodge',
  'EFFA', 'Ferrari', 'Fiat', 'Ford', 'GWM', 'Haval', 'Honda', 'Hyundai',
  'Iveco', 'JAC', 'Jaguar', 'Jeep', 'Kia', 'Lamborghini', 'Land Rover',
  'Lexus', 'Lifan', 'Mercedes-Benz', 'Mini', 'Mitsubishi', 'Nissan',
  'Peugeot', 'Porsche', 'RAM', 'Renault', 'Smart', 'Subaru', 'Suzuki',
  'Tata', 'Toyota', 'Troller', 'Volkswagen', 'Volvo', 'Outra',
];

const MODELS_BY_BRAND = {
  Audi: [
    'A1', 'A3', 'A4', 'A5', 'A6', 'A7', 'A8', 'e-tron', 'Q2', 'Q3',
    'Q4 e-tron', 'Q5', 'Q7', 'Q8', 'R8', 'RS3', 'RS4', 'RS5', 'RS6',
    'RS7', 'TT', 'Outro',
  ],
  BMW: [
    'i3', 'i4', 'i7', 'iX', 'iX1', 'iX3', 'M2', 'M3', 'M4', 'M5', 'M8',
    'Série 1', 'Série 2', 'Série 3', 'Série 4', 'Série 5', 'Série 6',
    'Série 7', 'Série 8', 'X1', 'X2', 'X3', 'X4', 'X5', 'X6', 'X7', 'Z4',
    'Outro',
  ],
  BYD: [
    'Atto 3', 'Dolphin', 'Han', 'King', 'Seal', 'Song Plus', 'Tan',
    'Yuan Plus', 'Outro',
  ],
  Chery: [
    'Arrizo 5', 'Arrizo 6', 'iCar', 'QQ', 'Tiggo 2', 'Tiggo 3x', 'Tiggo 5x',
    'Tiggo 7', 'Tiggo 8', 'Outro',
  ],
  'Caoa Chery': [
    'Arrizo 5', 'Arrizo 6', 'iCar', 'QQ', 'Tiggo 2', 'Tiggo 3x', 'Tiggo 5x',
    'Tiggo 7', 'Tiggo 8', 'Outro',
  ],
  Chevrolet: [
    'Agile', 'Astra', 'Blazer', 'Camaro', 'Captiva', 'Celta', 'Classic',
    'Cobalt', 'Corsa', 'Corsa Classic', 'Cruze', 'Equinox', 'Joy', 'Kadett',
    'Meriva', 'Monza', 'Montana', 'Omega', 'Onix', 'Onix Plus', 'Opala',
    'Prisma', 'S10', 'Silverado', 'Sonic', 'Spin', 'Suburban', 'Tracker',
    'Trailblazer', 'Vectra', 'Veraneio', 'Zafira', 'Outro',
  ],
  'Citroën': [
    'Aircross', 'Berlingo', 'C3', 'C3 Picasso', 'C4', 'C4 Cactus',
    'C4 Lounge', 'C4 Pallas', 'C5', 'C5 Aircross', 'DS3', 'DS4', 'DS5',
    'Jumper', 'Jumpy', 'Xsara', 'Xsara Picasso', 'Outro',
  ],
  Dodge: [
    'Avenger', 'Caliber', 'Challenger', 'Charger', 'Dakota', 'Durango',
    'Journey', 'Magnum', 'Ram 1500', 'Ram 2500', 'Outro',
  ],
  EFFA: ['JL150T', 'M100', 'Picape', 'Start', 'Van', 'Outro'],
  Ferrari: [
    '296 GTB', '488', '812', 'F8', 'Portofino', 'Purosangue', 'Roma', 'SF90',
    'Outro',
  ],
  Fiat: [
    '147', '500', 'Argo', 'Bravo', 'Cronos', 'Doblo', 'Ducato', 'Elba',
    'Fastback', 'Fiorino', 'Freemont', 'Grand Siena', 'Idea', 'Linea',
    'Marea', 'Mille', 'Mobi', 'Palio', 'Palio Weekend', 'Premio', 'Pulse',
    'Punto', 'Siena', 'Stilo', 'Strada', 'Tempra', 'Tipo', 'Toro', 'Uno',
    'Uno Mille', 'Outro',
  ],
  Ford: [
    'Belina', 'Bronco', 'Bronco Sport', 'Corcel', 'Courier', 'Del Rey',
    'EcoSport', 'Edge', 'Escort', 'F-1000', 'F-150', 'F-250', 'Fiesta',
    'Focus', 'Fusion', 'Galaxy', 'Ka', 'Ka+', 'Landau', 'Maverick',
    'Mondeo', 'Mustang', 'New Fiesta', 'Pampa', 'Ranger', 'Royale',
    'Taurus', 'Territory', 'Verona', 'Versailles', 'Outro',
  ],
  GWM: [
    'Haval H2', 'Haval H6', 'Haval Jolion', 'Ora 03', 'Ora 07', 'Poer',
    'Tank 300', 'Tank 500', 'Outro',
  ],
  Haval: ['H2', 'H6', 'Jolion', 'Outro'],
  Honda: [
    'Accord', 'City', 'Civic', 'CR-V', 'Fit', 'HR-V', 'Pilot', 'WR-V',
    'ZR-V', 'Outro',
  ],
  Hyundai: [
    'Atos', 'Azera', 'Creta', 'Elantra', 'Genesis', 'Getz', 'HB20', 'HB20S',
    'HB20X', 'i30', 'i30 CW', 'Ioniq', 'Ioniq 5', 'Ioniq 6', 'ix35', 'Kona',
    'Santa Fe', 'Sonata', 'Tucson', 'Veloster', 'Vera Cruz', 'Outro',
  ],
  Iveco: ['Daily', 'Tector', 'Outro'],
  JAC: [
    'e-JS1', 'e-JS4', 'iEV20', 'iEV40', 'J3', 'J5', 'J6', 'T40', 'T50',
    'T60', 'T80', 'Outro',
  ],
  Jaguar: [
    'E-Pace', 'F-Pace', 'F-Type', 'I-Pace', 'XE', 'XF', 'XJ', 'Outro',
  ],
  Jeep: [
    'Cherokee', 'Commander', 'Compass', 'Gladiator', 'Grand Cherokee',
    'Renegade', 'Wrangler', 'Outro',
  ],
  Kia: [
    'Bongo', 'Carens', 'Carnival', 'Cerato', 'EV6', 'Mohave', 'Niro',
    'Picanto', 'Rio', 'Seltos', 'Sorento', 'Soul', 'Sportage', 'Stinger',
    'Stonic', 'Telluride', 'Outro',
  ],
  Lamborghini: ['Aventador', 'Huracán', 'Revuelto', 'Urus', 'Outro'],
  'Land Rover': [
    'Defender', 'Discovery', 'Discovery Sport', 'Freelander', 'Range Rover',
    'Range Rover Evoque', 'Range Rover Sport', 'Range Rover Velar', 'Outro',
  ],
  Lexus: ['ES', 'GS', 'IS', 'LS', 'NX', 'RX', 'UX', 'Outro'],
  Lifan: ['320', '530', '620', '720', 'X60', 'X70', 'Outro'],
  'Mercedes-Benz': [
    'AMG GT', 'Classe A', 'Classe B', 'Classe C', 'Classe CLA', 'Classe CLS',
    'Classe E', 'Classe G', 'Classe GLA', 'Classe GLB', 'Classe GLC',
    'Classe GLE', 'Classe GLS', 'Classe S', 'Classe SL', 'EQA', 'EQB', 'EQC',
    'EQE', 'EQS', 'GLA', 'GLC', 'Sprinter', 'Vito', 'Outro',
  ],
  Mini: ['Clubman', 'Cooper', 'Cooper S', 'Countryman', 'Outro'],
  Mitsubishi: [
    'ASX', 'Eclipse Cross', 'L200', 'Lancer', 'Outlander', 'Outlander Sport',
    'Pajero', 'Pajero Dakar', 'Pajero Full', 'Pajero Sport', 'Pajero TR4',
    'Outro',
  ],
  Nissan: [
    '370Z', 'Altima', 'Frontier', 'GT-R', 'Kicks', 'Leaf', 'Livina', 'March',
    'Murano', 'Note', 'Pathfinder', 'Sentra', 'Tiida', 'Versa', 'X-Trail',
    'Xterra', 'Outro',
  ],
  Peugeot: [
    '106', '107', '205', '206', '207', '208', '2008', '3008', '306', '307',
    '308', '405', '406', '407', '408', '5008', '508', 'Hoggar', 'Partner',
    'RCZ', 'Outro',
  ],
  Porsche: [
    '718 Boxster', '718 Cayman', '911', 'Cayenne', 'Macan', 'Panamera',
    'Taycan', 'Outro',
  ],
  RAM: ['700', '1000', '1500', '2500', '3500', 'Rampage', 'Outro'],
  Renault: [
    'Captur', 'Clio', 'Duster', 'Fluence', 'Kangoo', 'Kardian', 'Kwid',
    'Logan', 'Master', 'Mégane', 'Oroch', 'Sandero', 'Scénic', 'Stepway',
    'Symbol', 'Twingo', 'Zoe', 'Outro',
  ],
  Smart: ['ForFour', 'ForTwo', 'Outro'],
  Subaru: ['BRZ', 'Forester', 'Impreza', 'Legacy', 'Outback', 'WRX', 'XV', 'Outro'],
  Suzuki: ['Grand Vitara', 'Jimny', 'S-Cross', 'Swift', 'Vitara', 'Outro'],
  Tata: ['Indica', 'Indigo', 'Prima', 'Xenon', 'Outro'],
  Toyota: [
    '4Runner', 'Bandeirante', 'Camry', 'Corolla', 'Corolla Cross', 'Etios',
    'Fielder', 'Fortuner', 'Hilux', 'Land Cruiser', 'Prius', 'RAV4', 'SW4',
    'Yaris', 'Outro',
  ],
  Troller: ['T4', 'TX4', 'Outro'],
  Volkswagen: [
    'Amarok', 'Apollo', 'Bora', 'Brasília', 'Crossfox', 'Fox', 'Fusca',
    'Gol', 'Golf', 'ID.4', 'Jetta', 'Kombi', 'Logus', 'New Beetle', 'Nivus',
    'Parati', 'Passat', 'Polo', 'Quantum', 'Santana', 'Saveiro', 'SpaceFox',
    'T-Cross', 'Taos', 'Tiguan', 'Touareg', 'Up!', 'Variant', 'Virtus',
    'Voyage', 'Outro',
  ],
  Volvo: [
    'C40', 'EX30', 'EX90', 'S60', 'S90', 'V40', 'V60', 'V90', 'XC40',
    'XC60', 'XC90', 'Outro',
  ],
};

const COLORS_LIST = [
  'Branco', 'Preto', 'Prata', 'Cinza', 'Vermelho', 'Azul', 'Verde', 'Amarelo',
  'Laranja', 'Marrom', 'Bege', 'Dourado', 'Vinho', 'Grafite', 'Bronze', 'Outra',
];

const _currentYear = new Date().getFullYear();
/** Array of model years from current+1 down to 1950 */
const YEARS = Array.from(
  { length: _currentYear + 2 - 1950 },
  (_, i) => String(_currentYear + 1 - i),
);

// ── Alert status ──────────────────────────────────────────────────────────────

/** Stolen car report lifecycle statuses */
const ALERT_STATUS = {
  ACTIVE: 'active',
  RESOLVED: 'resolved',
  ALL: 'all',
};

// ── Public sightings anonymization ────────────────────────────────────────────

/** Number of plate characters shown in the public sightings view */
const PUBLIC_PLATE_VISIBLE_CHARS = 4;

/** Mask appended after the visible plate characters in the public view */
const PUBLIC_PLATE_MASK = '•••';

// ── Exports ───────────────────────────────────────────────────────────────────

module.exports = {
  APP_NAME,
  APP_DESCRIPTION,
  BRAND_TAGLINE,
  COLORS,
  SECURITY_COLORS,
  NOTIFICATION_RADIUS_KM,
  DEFAULT_ALERT_RADIUS_KM,
  MIN_ALERT_RADIUS_KM,
  MAX_ALERT_RADIUS_KM,
  STORAGE_KEYS,
  SECURITY_STORAGE_KEYS,
  INACTIVITY_OPTIONS,
  BRANDS,
  MODELS_BY_BRAND,
  COLORS_LIST,
  YEARS,
  ALERT_STATUS,
  PUBLIC_PLATE_VISIBLE_CHARS,
  PUBLIC_PLATE_MASK,
};
